import { isTimestampLike, timestampToDate } from './FirestoreUtils';

export const MILLISECOND = 1;
export const SECOND = MILLISECOND * 1000;
export const MINUTE = SECOND * 60;
export const HOUR = MINUTE * 60;
export const DAY = HOUR * 24;
export const WEEK = DAY * 7;


// Creates a function that passes any of the application's DateTime-like
// objects to the given `Intl.DateTimeFormat`. Runtime type checks are
// performed, with a consistent fallback string if the argument is not
// compatible.
function makeDateTimeFormatFunctionWithFallback(
    formatter: Intl.DateTimeFormat
) {
    return (x: unknown) => {
        if (isTimestampLike(x)) {
            return formatter.format(timestampToDate(x));
        }
        if (x instanceof Date) {
            return formatter.format(x);
        }
        if (typeof x === 'number' && isFinite(x)) {
            return formatter.format(new Date(x));
        }
        return '-';
    };
}

export const FORMATTER_DATETIME_MED_SECONDS = new Intl.DateTimeFormat(
    undefined,
    {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
    }
);
export const formatDateTimeMedSeconds = makeDateTimeFormatFunctionWithFallback(
    FORMATTER_DATETIME_MED_SECONDS
);

export const FORMATTER_DATETIME_MED_MINUTES = new Intl.DateTimeFormat(
    undefined,
    {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }
);
export const formatDateTimeMedMinutes = makeDateTimeFormatFunctionWithFallback(
    FORMATTER_DATETIME_MED_MINUTES
);

export const FORMATTER_DATE_SHORT = new Intl.DateTimeFormat(undefined, {
    year: '2-digit',
    month: 'short',
    day: 'numeric',
});
export const formatDateShort =
    makeDateTimeFormatFunctionWithFallback(FORMATTER_DATE_SHORT);

export const FORMATTER_DATETIME_LONG_MINUTES = new Intl.DateTimeFormat(
    undefined,
    {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }
);
export const formatDateTimeLongMinutes = makeDateTimeFormatFunctionWithFallback(
    FORMATTER_DATETIME_LONG_MINUTES
);

const RELATIVE_FORMATTER = new Intl.RelativeTimeFormat();
export function formatAdaptiveRelativeTime(
    date: Date,
    relativeTo: Date,
    fallback = ''
) {
    const posDelta = relativeTo.getTime() - date.getTime();
    if (!isFinite(posDelta) || posDelta < -5 * SECOND) return fallback;
    if (posDelta < 5 * SECOND) return 'Just now';
    if (posDelta < MINUTE)
        return RELATIVE_FORMATTER.format(
            -Math.round(posDelta / SECOND),
            'second'
        );
    if (posDelta < HOUR)
        return RELATIVE_FORMATTER.format(
            -Math.round(posDelta / MINUTE),
            'minute'
        );
    return FORMATTER_DATETIME_MED_MINUTES.format(date);
}
